import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { ReverLoading, toast } from '@itsrever/design-system'
import {
  ListWithdrawalRequestsStatusesEnum as Status,
  type PbWithdrawalRequest
} from '@itsrever/rever-api'
import { useAppSelector } from '@/redux/hooks'
import { Roles } from '@/utils'
import { useWithdrawalSettings } from '@/hooks/use-withdrawal-settings'
import {
  WITHDRAWALS_PAGE_SIZE,
  useSetWithdrawalClosed,
  useWithdrawalFilters,
  useWithdrawalRequests
} from '@/hooks/use-withdrawal-requests'
import NotFound from '@/ui/pages/404.page'
import { ListTabs, useListTabsPage } from '@/ui/shared/list-tabs'
import { ExportWithdrawalsButton } from './withdrawals/export-withdrawals-button'
import { useWithdrawalColumns } from './withdrawals/withdrawals-columns'
import { WithdrawalsFilters } from './withdrawals/withdrawals-filters'

const OPEN = 'open'
const DONE = 'done'
const ALL = 'all'

type Statuses = Status[]

// A refund ends the request; closing it by hand covers what REVER can't see.
const DONE_STATUSES: Statuses = [Status.Refunded, Status.Closed]
const OPEN_STATUSES: Statuses = [
  Status.Received,
  Status.AwaitingReturn,
  Status.Returned
]

// A tab narrows the statuses picked in the filter bar; null when nothing can match.
const tabStatuses = (tab: string, picked: Statuses): Statuses | null => {
  if (tab === ALL) return picked
  const within = tab === OPEN ? OPEN_STATUSES : DONE_STATUSES
  const statuses = picked.length
    ? picked.filter((s) => within.includes(s))
    : within
  return statuses.length ? statuses : null
}

export default function WithdrawalsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const ecommerceId = useAppSelector((s) => s.generalData.selectedEcommerce)
  const role = useAppSelector((s) => s.userApi.getMe.response.user?.role)
  const storeSettings = useAppSelector((s) => s.settingsApi.settings.response)
  // A reviewer only sees its warehouses' returns, and these requests belong to no warehouse.
  const settings = useWithdrawalSettings(
    role === Roles.Reviewer ? undefined : ecommerceId
  )
  const { currentTab, pageIndex, offsetFor } = useListTabsPage(
    OPEN,
    WITHDRAWALS_PAGE_SIZE
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useWithdrawalFilters()
  const listed = settings.enabled ? ecommerceId : undefined
  const openStatuses = tabStatuses(OPEN, filters.statuses)
  const doneStatuses = tabStatuses(DONE, filters.statuses)
  const open = useWithdrawalRequests(
    openStatuses ? listed : undefined,
    offsetFor(OPEN),
    { ...filters, statuses: openStatuses ?? [] }
  )
  const done = useWithdrawalRequests(
    doneStatuses ? listed : undefined,
    offsetFor(DONE),
    { ...filters, statuses: doneStatuses ?? [] }
  )
  const all = useWithdrawalRequests(listed, offsetFor(ALL), filters)
  // Closing or reopening the last row of a page empties it: step back to the last page that has rows.
  const page = { [OPEN]: open, [DONE]: done, [ALL]: all }[currentTab]?.data
  useEffect(() => {
    const count = Number(page?.rowcount ?? 0)
    if (!page || page.withdrawal_requests?.length || !count || !pageIndex)
      return
    const last = Math.ceil(count / WITHDRAWALS_PAGE_SIZE) - 1
    if (last > 0) searchParams.set('page', String(last))
    else searchParams.delete('page')
    setSearchParams(searchParams, { replace: true })
  }, [page, pageIndex, searchParams, setSearchParams])
  const setClosed = useSetWithdrawalClosed(ecommerceId ?? undefined)
  const { mutate } = setClosed
  const toggleClosed = useCallback(
    (w: PbWithdrawalRequest) =>
      mutate(
        { id: w.id ?? '', closed: !w.closed_at },
        {
          onError: () =>
            toast({ text: t('withdrawals.close_error'), variant: 'error' })
        }
      ),
    [mutate, t]
  )
  const columns = useWithdrawalColumns(
    {
      canClose: role === Roles.ReadWrite,
      pendingId: setClosed.isPending ? setClosed.variables?.id : undefined,
      toggle: toggleClosed
    },
    ecommerceId,
    // Settings still loading for another store would link its admin.
    storeSettings.ecommerce_id === ecommerceId
      ? storeSettings.shopify_settings?.store_id
      : undefined
  )

  if (settings.loading) return <ReverLoading />
  if (!settings.enabled || !ecommerceId) return <NotFound />

  const openReturn = (w: PbWithdrawalRequest) => {
    if (w.process_id) navigate(`/returns/orders/${w.process_id}`)
  }

  return (
    <div className="relative h-full">
      <div className="min-h-full bg-neutral-100/10 p-4">
        <div className="flex items-center justify-between gap-3.5 border-b border-solid border-neutral-200 pb-4">
          <WithdrawalIcon />
          <header className="mr-auto space-y-1">
            <h1 className="font-[Roobert] text-lg font-medium leading-6 text-neutral-900">
              {t('withdrawals.title')}
            </h1>
            <h2 className="font-[Roobert] text-sm font-normal leading-5 text-neutral-600">
              {t('withdrawals.subtitle')}
            </h2>
          </header>
          <ExportWithdrawalsButton
            ecommerceId={ecommerceId}
            filters={filters}
          />
        </div>
        <WithdrawalsFilters />
        <ListTabs
          tabs={[
            tabFrom(OPEN, t('withdrawals.open'), openStatuses && open),
            tabFrom(DONE, t('withdrawals.done'), doneStatuses && done),
            tabFrom(ALL, t('withdrawals.all'), all)
          ]}
          allTab={OPEN}
          pageSize={WITHDRAWALS_PAGE_SIZE}
          columns={columns}
          onRowClick={openReturn}
          pinFirstColumn
        />
      </div>
    </div>
  )
}

// A tab whose statuses can't match lists nothing, without asking the API.
const tabFrom = (
  value: string,
  label: string,
  list: ReturnType<typeof useWithdrawalRequests> | null
) => ({
  value,
  label,
  rows: list ? list.data && (list.data.withdrawal_requests ?? []) : [],
  count: Number(list?.data?.rowcount ?? 0),
  isPending: !!list?.isPending,
  isFetching: !!list?.isFetching
})

// The same circle as the Orders and Items headers, so the Returns pages read as one section.
const WithdrawalIcon = () => (
  <div className="flex select-none items-center justify-center overflow-hidden rounded-full border border-solid border-neutral-200 bg-white p-3 shadow-sm">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={22}
      height={22}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-neutral-700"
      aria-hidden
    >
      <path d="M14 3v4a1 1 0 0 0 1 1h4" />
      <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
      <path d="M10 12l4 4m0 -4l-4 4" />
    </svg>
  </div>
)
